import * as E from 'fp-ts/Either';
import {
  Box,
  Button,
  Checkbox,
  Group,
  PasswordInput,
  TextInput,
} from '@mantine/core';
import { User, update, insert } from '../api/users';
import { ChangeEvent, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

type UserFormData = {
  password: string;
  confirmPassword: string;
  username: string;
  roles: string[];
};

export const UserForm = ({ user }: { user: User | null }) => {
  const [changePassword, setChangePassword] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    username: user?.username ?? '',
    password: '',
    confirmPassword: '',
    roles: user?.roles ?? ['user'],
  });

  const auth = useAuth();
  const navigate = useNavigate();

  const updateValue =
    (k: keyof UserFormData) => (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((data) => ({
        ...data,
        [k]: e.target.value,
      }));
    };

  const updateRoles = (roles: string[]) =>
    setFormData((data) => ({ ...data, roles }));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!auth?.token) {
      return;
    }

    if (!user && !formData.password) {
      toast.error('Passwords is required for new users');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!formData.username) {
      toast.error('Username is required');
      return;
    }

    const record: Partial<User & { password: string }> = {};
    if (formData.username) {
      record.username = formData.username;
    }
    if (formData.password) {
      record.password = formData.password;
    }
    if (formData.roles) {
      record.roles = formData.roles;
    }

    const result = user
      ? await update(auth.token, user._id, record)
      : await insert(auth.token, record);

    if (E.isLeft(result)) {
      toast.error(result.left.message);
    } else {
      toast.success('User saved');
      if (!user) {
        navigate(`/users/${result.right._id}`);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextInput
        value={formData.username}
        onChange={updateValue('username')}
        label="Username"
        name="username"
        mb="md"
        required
      />
      <Checkbox.Group
        my="md"
        value={formData.roles}
        onChange={updateRoles}
        label="Allowed roles"
      >
        <Group mt="sm">
          <Checkbox label="User" value="user" />
          <Checkbox label="Manage users" value="manage-users" />
          <Checkbox label="Audit apps and license changes" value="audit-apps" />
          <Checkbox label="Audit admin-user changes" value="audit-users" />
        </Group>
      </Checkbox.Group>
      {!user || changePassword ? (
        <>
          <PasswordInput
            value={formData.password}
            onChange={updateValue('password')}
            label="Password"
            name="password"
            mb="md"
            required={!user}
          />
          <PasswordInput
            value={formData.confirmPassword}
            onChange={updateValue('confirmPassword')}
            label="Confirm password"
            name="confirmPassword"
            mb="md"
            required={!user}
          />
        </>
      ) : (
        <Box mb="md">
          <Button onClick={() => setChangePassword(true)} variant="outline">
            Show reset password
          </Button>
        </Box>
      )}
      <Box>
        <Button color="blue" type="submit">
          {user ? 'Save user' : 'Create user'}
        </Button>
      </Box>
    </form>
  );
};
